import { Money } from './money'
import { JobStatus } from './worker-shift'

export enum TimesheetSource {
  INITIAL = 'initial',
  ADJUSTMENTS = 'adjustments',
  MIGRATIONS = 'migrations',
}

export enum TimesheetStatus {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
}

export enum ExternalTimesheetSource {
  PAYLOCITY = 'PAYLOCITY',
  TIMECO = 'TIMECO',
  DEPUTY = 'DEPUTY',
  KRONOS = 'KRONOS',
  UATTEND = 'UATTEND',
  OTHER = 'OTHER',
}

export interface ExternalTimesheetWorker {
  id: string
  createdAt: string
  updatedAt: string
  externalId: string
  source: ExternalTimesheetSource
  workerId: string
  companyId: string
}

export interface UpsertExternalTimesheetWorkerInput {
  workerId: string
  externalId: string
  source: ExternalTimesheetSource
  companyId: string
}

export enum WorkerClockInMethod {
  NONE = 'NONE',
  QR_CODE = 'QR_CODE',
  PHONE_NUMBER = 'PHONE_NUMBER',
  SUPERVISOR = 'SUPERVISOR',
  TRABA_APP = 'TRABA_APP',
}

export interface TimekeepingConfig {
  id: string
  companyId: string
  locationId?: string
  workerClockInMethod: WorkerClockInMethod
  externalTimesheetSource?: ExternalTimesheetSource
  kioskClockPhotoEnabled?: boolean
  kioskBreakPhotoEnabled?: boolean
  createdAt: Date
  updatedAt: Date
}

export interface Timesheet {
  companyId: string
  createdAt: Date
  timesheetId?: string
  source: TimesheetSource
  totalTimeWorked: number
  grossPayToWorkers: Money
  totalCharge: Money
  totalBilledTime: number
  totalPaidTime?: number
  totalUnitsWorked?: number
  totalPaidUnits?: number
  shiftId: string
  timesheetApprovalStatus: {
    isApproved: boolean
    approvedAt?: Date
    isPendingEdit?: boolean
  }
}

export const COMPLETED_JOB_STATUSES_FOR_TIMESHEETS = [
  JobStatus.Complete,
  JobStatus.Abandoned,
]

/**
 * Drives `<TimesheetDataPanel>` rendering: render the record, show an
 * empty state, surface a sync error, or note that the company isn't
 * integrated. Mirror of the backend enum at
 * `apps/traba-server-node/src/models/worker-shifts/types/timesheet-data.types.ts`.
 */
export enum TimesheetDataStatus {
  SUCCESS = 'SUCCESS',
  NOT_FOUND = 'NOT_FOUND',
  SYNC_FAILED = 'SYNC_FAILED',
  NOT_INTEGRATED = 'NOT_INTEGRATED',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
}

/**
 * `'NONE'` is returned when a company has no `externalTimesheetSource`
 * configured (paired with `status: NOT_INTEGRATED`). Otherwise this
 * widens to one of the `ExternalTimesheetSource` enum values.
 */
export type TimesheetDataSource = ExternalTimesheetSource | 'NONE'

/**
 * Wire format is `Date` on the backend but JSON-serializes to an ISO
 * string in transit; the FE deserializes it as `string`.
 */
export type ExternalTimesheetBreak = {
  startTime: string
  endTime?: string | null
  durationMinutes?: number
}

export type ExternalTimesheetRecord = {
  clockInTime?: string
  clockOutTime?: string
  totalMinutes?: number
  breaks?: ExternalTimesheetBreak[]
  /** Free-form note from the integration (e.g. employee timecard status). */
  remoteStatus?: string
}

export type TimesheetDataSync = {
  /** When we last successfully refreshed this record from the integration. */
  lastSyncedAt?: string
  /** Set when the most recent sync attempt failed. */
  lastSyncError?: string
}

/** Fields shared by every variant of `TimesheetDataResponse`. */
interface TimesheetDataBase {
  workerShiftId: string
}

/** Adapter found a record for this worker-shift; render `record` + optional `deepLink`. */
export interface TimesheetDataSuccess extends TimesheetDataBase {
  source: ExternalTimesheetSource
  status: TimesheetDataStatus.SUCCESS
  record: ExternalTimesheetRecord
  /** Adapter-provided link to the external system's view of this timesheet. */
  deepLink?: string
}

/** Integration ran but no timesheet exists for this shift yet. */
export interface TimesheetDataNotFound extends TimesheetDataBase {
  source: ExternalTimesheetSource
  status: TimesheetDataStatus.NOT_FOUND
}

/** Adapter call failed (e.g. upstream 503); show the error and offer retry. */
export interface TimesheetDataSyncFailed extends TimesheetDataBase {
  source: ExternalTimesheetSource
  status: TimesheetDataStatus.SYNC_FAILED
  sync: TimesheetDataSync & { lastSyncError: string }
}

/** Company has no `externalTimesheetSource` configured. `source` widens to `'NONE'`. */
export interface TimesheetDataNotIntegrated extends TimesheetDataBase {
  source: 'NONE'
  status: TimesheetDataStatus.NOT_INTEGRATED
}

/** Company configured a source we haven't wired an adapter for yet. */
export interface TimesheetDataNotSupported extends TimesheetDataBase {
  source: ExternalTimesheetSource
  status: TimesheetDataStatus.NOT_SUPPORTED
}

/**
 * Response from `GET /worker-shifts/:workerShiftId/timesheet-data`.
 *
 * Discriminated by `status` — switching on it narrows `data` to the
 * corresponding variant inside each `case` (so `record`, `sync`, etc.
 * become non-optional where the BE guarantees them). Mirror of the
 * BE union at
 * `apps/traba-server-node/src/models/worker-shifts/types/timesheet-data.types.ts`.
 */
export type TimesheetDataResponse =
  | TimesheetDataSuccess
  | TimesheetDataNotFound
  | TimesheetDataSyncFailed
  | TimesheetDataNotIntegrated
  | TimesheetDataNotSupported
